import ReactApexChart from 'react-apexcharts';
import { withNamespaces } from 'react-i18next';
import { Card, CardBody, CardTitle, Col, Row } from 'reactstrap';

import ChartError from 'src/modules/3rd-party-management/pages/admin/compliance/components/dashboard/ChartError';

import useSupplierOverviewSearchOverview from './hooks/useSupplierOverviewSearchOverview';

const MonthKeys = {
    '1': 'Jan',
    '2': 'Feb',
    '3': 'Mar',
    '4': 'Apr',
    '5': 'May',
    '6': 'Jun',
    '7': 'Jul',
    '8': 'Aug',
    '9': 'Sep',
    '10': 'Oct',
    '11': 'Nov',
    '12': 'Dec',
};

const KPI_CONFIG = [
    {
        key: 'totalSearchItems',
        label: 'Total Search Items',
        modifier: 'green',
        color: '#1CBB8C',
    },
    {
        key: 'monitoredItems',
        label: 'Monitored Items',
        modifier: 'indigo',
        color: '#5664D2'
    },
    {
        key: 'screenedItems',
        label: 'Screened Items',
        modifier: 'orange',
        color: '#F2994A'
    },
];

const SearchOverviewSkeleton = () => (
    <Card className="overview-search-overview mb-4">
        <CardBody>
            <div className="dt-field dt-skeleton mb-4" style={{ width: '25%' }} />
            <Row className="g-4">
                <Col md="3">
                    <div className="dt-field dt-skeleton mb-3" style={{ width: '100%', height: '72px' }} />
                    <div className="dt-field dt-skeleton mb-3" style={{ width: '100%', height: '72px' }} />
                    <div className="dt-field dt-skeleton" style={{ width: '100%', height: '72px' }} />
                </Col>
                <Col md="9">
                    <div className="dt-field dt-skeleton" style={{ width: '100%', height: '320px' }} />
                </Col>
            </Row>
        </CardBody>
    </Card>
);

const SearchOverview = ({
    t,
    supplierId,
    hasComplianceMonitoringFeature,
    hasComplianceScreeningFeature
}) => {
    const fetchSearchOverviewQuery = useSupplierOverviewSearchOverview({
        supplierId,
        t,
    });

    const visibleKpiConfig = KPI_CONFIG.filter((kpi) => {
        if (kpi.key === 'monitoredItems') {
            return !!hasComplianceMonitoringFeature;
        }

        if (kpi.key === 'screenedItems') {
            return !!hasComplianceScreeningFeature;
        }

        return true;
    });

    if (fetchSearchOverviewQuery.isLoading || fetchSearchOverviewQuery.isFetching) {
        return <SearchOverviewSkeleton />;
    }

    const {
        kpis = {},
        monthlyTrends = [],
    } = fetchSearchOverviewQuery.data || {};

    const labels = [];
    const monthlyDataByKey = visibleKpiConfig.reduce((acc, kpi) => {
        acc[kpi.key] = [];
        return acc;
    }, {});

    monthlyTrends.forEach((element) => {
        labels.push(
            MonthKeys[parseInt((element.month || '').split('-')?.[1] || '0')?.toString()]
        );

        visibleKpiConfig.forEach((kpi) => {
            monthlyDataByKey[kpi.key].push(element[kpi.key] || 0);
        });
    });

    const chartSeries = visibleKpiConfig.map((kpi) => ({
        name: t(kpi.label),
        type: kpi.key === 'totalSearchItems' ? 'column' : 'line',
        data: monthlyDataByKey[kpi.key],
    }));

    const chartColors = visibleKpiConfig.map((kpi) => kpi.color);
    const strokeWidths = visibleKpiConfig.map((kpi) => (
        kpi.key === 'totalSearchItems' ? 0 : 3
    ));
    const markerSizes = visibleKpiConfig.map((kpi) => (
        kpi.key === 'totalSearchItems' ? 0 : 5
    ));
    const totalSearchSeriesIndex = visibleKpiConfig.findIndex(
        (kpi) => kpi.key === 'totalSearchItems'
    );

    const chartOptions = {
        colors: chartColors,
        chart: {
            toolbar: {
                show: false,
            },
            zoom: {
                enabled: false,
            },
        },
        legend: {
            show: true,
            position: 'bottom',
            horizontalAlign: 'center',
        },
        stroke: {
            curve: 'smooth',
            width: strokeWidths,
        },
        markers: {
            size: markerSizes,
            strokeColors: '#FFFFFF',
            strokeWidth: 2,
            hover: {
                size: 7,
            },
        },
        plotOptions: {
            bar: {
                columnWidth: '55%',
                borderRadius: 3,
            },
        },
        dataLabels: {
            enabled: totalSearchSeriesIndex >= 0,
            enabledOnSeries: totalSearchSeriesIndex >= 0 ? [totalSearchSeriesIndex] : [],
            style: {
                fontSize: '12px',
                colors: ['#FFFFFF'],
            },
            background: {
                enabled: false,
            },
            formatter(val) {
                return val;
            },
        },
        xaxis: {
            categories: labels,
            axisBorder: {
                show: true,
            },
            axisTicks: {
                show: false,
            },
            labels: {
                formatter: (val) => {
                    if (!val) return null;
                    const splitedVal = val.toString().split(' ');
                    return `${t(splitedVal[0])} ${t(splitedVal[1])}`;
                },
            },
        },
        yaxis: {
            show: true,
            labels: {
                formatter: (val) => val.toFixed(0),
            },
        },
        tooltip: {
            theme: 'light',
            enabled: true,
            shared: true,
            intersect: false,
            followCursor: true,
        },
    };

    return (
        <Card className="overview-search-overview mb-4">
            <CardBody>
                <CardTitle tag="h6" className="overview-section-title mb-4">
                    {t('Search Overview')}
                </CardTitle>

                <Row className="g-4 align-items-stretch">
                    <Col md="3">
                        <div className="overview-search-overview__kpis">
                            {visibleKpiConfig.map((kpi) => (
                                <div
                                    key={kpi.key}
                                    className={`overview-search-overview__kpi overview-search-overview__kpi--${kpi.modifier}`}
                                >
                                    <span className="overview-search-overview__kpi-label">
                                        {t(kpi.label)}
                                    </span>
                                    <span className="overview-search-overview__kpi-value">
                                        {kpis[kpi.key] ?? 0}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </Col>

                    <Col md="9">
                        {fetchSearchOverviewQuery.isError ? (
                            <ChartError
                                title={
                                    fetchSearchOverviewQuery.error?.response?.status === 403
                                        ? 'Error 403'
                                        : null
                                }
                                message={
                                    fetchSearchOverviewQuery.error?.response?.status === 403
                                        ? 'Please upgrade your plan to access this feature'
                                        : null
                                }
                                onRetry={fetchSearchOverviewQuery.refetch}
                            />
                        ) : (
                            <ReactApexChart
                                options={chartOptions}
                                series={chartSeries}
                                height="320px"
                            />
                        )}
                    </Col>
                </Row>
            </CardBody>
        </Card>
    );
};

export default withNamespaces()(SearchOverview);
